import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import isObject from "lodash/isObject";
import moment from "moment";
import { toSnakeCase } from "../../../utils/ObjectUtil";
const ecpApi = AiseaApiFactory.get("enterpriseCrewPayroll");
const emvRepository = AiseaApiFactory.get("enterpriseManningVessel");

const EXPORT_IDS = {
  PAYROLL: 1,
  ANALYSIS: 2,
}; // エクスポートするデータの種類
export const PERIOD_FORMAT = "YYYY-MM-DD";
// リクエストパラメータが不正な場合のエラーメッセージ
const ERROR_MESSAGES = {
  INVALID_PERIOD: "Invalid period",
  INVALID_ENTERPRISE_MANNING_VESSEL_IDS: "Invalid enterpriseManningVesselIds",
  CONFLICT: "Conflict",
};

const ERROR_STATUS_CODES = {
  CONFLICT: 409,
};
const MIN_PERIOD_DAYS = 1; // エクスポートで指定可能な最小の日数
const MAX_PERIOD_DAYS = 31; // エクスポートで指定可能な最大の日数
const ENTERPRISE_MANNING_VESSEL_IDS_LENGTH = 1; // 現状は1つのidのみ指定可能

class EnterpriseCrewPayrollService {
  // 不正なEnterpriseManningVesselIdsを指定した時のエラー
  // 主にリクエストの確認
  static InvalidPeriodError = class extends Error {
    constructor(message = ERROR_MESSAGES.INVALID_PERIOD, options) {
      super(message);
      this.name = "InvalidPeriodError";
      this.options = options;
    }
  };
  // 不正なEnterpriseManningVesselIdsを指定した時のエラー
  // 主にリクエストの確認
  static InvalidEnterpriseManningVesselIdsError = class extends Error {
    constructor(
      message = ERROR_MESSAGES.InvalidEnterpriseManningVesselIdsError,
      options
    ) {
      super(message);
      this.name = "InvalidEnterpriseManningVesselIdsError";
      this.options = options;
    }
  };
  // apiが409の時に投げるエラー
  static ConflictError = class extends Error {
    constructor(
      message = ERROR_MESSAGES.CONFLICT,
      options = {
        code: ERROR_STATUS_CODES.CONFLICT,
      }
    ) {
      super(message);
      this.name = "ConflictError";
      this.options = options;
    }
  };

  // 連携データを取得
  async exportPayroll(
    routeParams // {startDate: Date | `${YYYY-MM-DD}`; endDate: Date | `${YYYY-MM-DD}`;}
  ) {
    const rp = routeParams;
    if (!this.isValidPeriod(rp)) {
      throw new EnterpriseCrewPayrollService.InvalidPeriodError();
    }
    const payload = this.createPayload(rp);
    const response = await ecpApi.export(EXPORT_IDS.PAYROLL, payload);
    return response;
  }

  // 分析用データ
  async exportForAnalysis(
    routeParams // {startDate: Date | `${YYYY-MM-DD}`; endDate: Date | `${YYYY-MM-DD}`; enterpriseManningVesselIds: `${number}`[] | number[];}
  ) {
    const rp = routeParams;
    if (!this.isValidManningVesselIds(rp)) {
      throw new EnterpriseCrewPayrollService.InvalidEnterpriseManningVesselIdsError();
    }
    if (!this.isValidPeriod(rp)) {
      throw new EnterpriseCrewPayrollService.InvalidPeriodError();
    }
    const payload = this.createPayload(rp);
    const response = await ecpApi.export(EXPORT_IDS.ANALYSIS, payload);
    return response;
  }

  // 連携データ・分析用データのエクスポート用のペイロードを作成
  createPayload(
    routeParams // {startDate: Date | `${YYYY-MM-DD}`; endDate: Date | `${YYYY-MM-DD}`; enterpriseManningVesselIds: `${number}`[] | number[];}
  ) {
    const {
      startDate,
      endDate,
      enterpriseManningVesselIds: enterprise_manning_vessel_ids,
    } = routeParams;
    const formattedStart = moment(startDate, PERIOD_FORMAT).format(
      PERIOD_FORMAT
    );
    const formattedEnd = moment(endDate, PERIOD_FORMAT).format(PERIOD_FORMAT);
    const payload = toSnakeCase({
      enterprise_manning_vessel_ids,
      startDate: formattedStart,
      endDate: formattedEnd,
    });
    return payload; // {start_date: `${YYYY-MM-DD}`; end_date: `${YYYY-MM-DD}`; enterprise_manning_vessel_ids: `${number}`[];}
  }

  // 船舶のセレクトボックス作成用データを取得
  async fetchManningVessels() {
    const res = await emvRepository.search({
      page: 0,
      size: 999, // 全ての船舶を取得するための指定
    });

    return res; // {manning_vessels: {id: `${number}`; name: string;}[]}
  }

  // 指定した期間が有効かどうか判定
  isValidPeriod = (
    rp // {startDate: Date | `${YYYY-MM-DD}`; endDate: Date | `${YYYY-MM-DD}`;}
  ) => {
    if (!isObject(rp)) return false;
    const { startDate, endDate } = rp;
    const start = moment(startDate, PERIOD_FORMAT);
    const end = moment(endDate, PERIOD_FORMAT);
    if (!start.isValid() || !end.isValid()) {
      return false;
    }
    const dayBeforeStart = moment(start).subtract(1, "days");
    const diffDays = end.diff(dayBeforeStart, "days"); // startDate, endDate を含めた期間の日数を計算するため、startDate の1日前からの日数を計算する
    return MIN_PERIOD_DAYS <= diffDays && diffDays <= MAX_PERIOD_DAYS;
  };

  // 船舶IDの指定した形式が有効かどうか判定
  isValidManningVesselIds = (
    rp // {enterpriseManningVesselIds: `${number}`[] | number[];}
  ) => {
    if (!isObject(rp)) return false;
    const { enterpriseManningVesselIds: vesselIds } = rp;
    if (!Array.isArray(vesselIds)) {
      return false;
    }
    const isValid =
      vesselIds.length === ENTERPRISE_MANNING_VESSEL_IDS_LENGTH &&
      vesselIds.every(id => Number.isInteger(id) || /^\d+$/.test(id));
    return isValid;
  };

  // apiレスポンスオブジェクトをチェックし、エラーがあれば例外をスローする
  checkResponse = (
    response // {status: boolean; code: undefined | number;}
  ) => {
    if (response.status) return true;
    switch (response.code) {
      case ERROR_STATUS_CODES.CONFLICT:
        throw new EnterpriseCrewPayrollService.ConflictError();
      default:
        throw new Error();
    }
  };

  isInvalidPeriodError = error => {
    return error instanceof EnterpriseCrewPayrollService.InvalidPeriodError;
  };

  // レスポンスが409の時のエラーか判定
  isConflictError = error => {
    return error instanceof EnterpriseCrewPayrollService.ConflictError;
  };
}

export default new EnterpriseCrewPayrollService();
